// src/components/admin/employee/EmployeeManagement.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import EmployeeDirectory from "./directory/EmployeeDirectory";
import EmployeeDetail from "./directory/EmployeeDetail";
import EmployeeAvailability from "./availability/EmployeeAvailability";
import EmployeePayRate from "./rate/EmployeePayRate";
import AdminPayrollViewer from "./payroll/AdminPayrollViewer"; 
import AdminLogging from "./logging/AdminLogging";
import { fetchTutors, updateTutorStatus, saveTutor } from "./directory/employeeFirestore";

export default function EmployeeManagement({ activeSubPage = "directory" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);

  const [directoryState, setDirectoryState] = useState({
    searchTerm: "",
    filterRole: "tutor",
    filterStatus: "Active",
    sortKey: "name",
  });

  const routeEmployeeId = useMemo(() => {
    const parts = location.pathname.replace(/\/+$/, "").split("/").filter(Boolean);
    const directoryIndex = parts.indexOf("directory");
    return directoryIndex >= 0 ? decodeURIComponent(parts[directoryIndex + 1] || "") : "";
  }, [location.pathname]);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const list = await fetchTutors();
      setEmployees(list);
    } catch (e) {
      console.error(e);
      // alert("Failed to load employees: " + e.message); 
      // Commented out alert to avoid spam on initial loads
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdateStatus(uid, statusLabel) {
    try {
      const lower = String(statusLabel).trim().toLowerCase();
      await updateTutorStatus(uid, lower);
      setEmployees((prev) => prev.map((x) => (x.id === uid ? { ...x, status: lower } : x)));
    } catch (e) {
      console.error(e);
      alert("Status update failed: " + e.message);
    }
  }

  async function handleUpdateRates(uid, patch) {
    try {
      await saveTutor(uid, patch);
      setEmployees((prev) =>
        prev.map((x) => (x.id === uid ? { ...x, ...patch } : x))
      );
    } catch (e) {
      console.error(e);
      alert("Rate update failed: " + e.message);
      throw e;
    }
  }

  // If in Detail view (drill down from Directory), show it regardless of subPage? 
  // Usually Detail view is a child of Directory.
  if (activeSubPage === "directory" && routeEmployeeId) {
    return (
      <EmployeeDetail
        uid={routeEmployeeId}
        onBack={() => {
          if (location.pathname !== "/employee/directory") {
            navigate("/employee/directory");
          }
        }}
      />
    );
  }

  // Render content based on activeSubPage prop passed from Sidebar
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, minHeight: 0 }}>
        {activeSubPage === "directory" && (
          <EmployeeDirectory
            employees={employees}
            loading={loading}
            onRefresh={load}
            onOpen={(uid) => {
              const nextPath = `/employee/directory/${encodeURIComponent(uid)}`;
              if (location.pathname !== nextPath) {
                navigate(nextPath);
              }
            }}
            onUpdateStatus={handleUpdateStatus}
            directoryState={directoryState}
            setDirectoryState={setDirectoryState}
          />
        )}
        {activeSubPage === "availability" && (
          <EmployeeAvailability employees={employees} />
        )}
        {activeSubPage === "rate" && (
          <EmployeePayRate
            employees={employees}
            loading={loading}
            onRefresh={load}
            onUpdateRates={handleUpdateRates}
          />
        )}
        {activeSubPage === "payroll" && (
          <AdminPayrollViewer />
        )}
        {activeSubPage === "logging" && (
          <AdminLogging />
        )}
      </div>
    </div>
  );
}
